/**
 * SlideShowUtilsComponents
 *
 * Small helpers to render common slideshow states
 * (no stations configured, error loading data, skeletons, etc.).
 *
 * summary:
 * - `renderNoStations`: card explaining no stations are configured
 * - `renderNonOperational`: wraps NonOperationalStation component
 * - `renderLoading`: lightweight placeholder for async loads
 * - `renderError`: friendly error card with context
 */
import React from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Stack,
  Alert,
  Paper,
  Skeleton,
} from "@mui/material";
import SettingsIcon from "@mui/icons-material/Settings";
import WarningIcon from "@mui/icons-material/Warning";

import NonOperationalStation from "../../WeatherDashboard/NonOperationalStation";

export function renderNoStations() {
  return (
    <Paper sx={{ p: 4, textAlign: "center" }}>
      <Typography variant="h4" sx={{ mb: 2 }}>
        No Stations Configured
      </Typography>
      <Typography>
        No weather stations have been configured for this kiosk display.
      </Typography>
    </Paper>
  );
}

export function renderNonOperational(stationName) {
  return <NonOperationalStation stationName={stationName} />;
}

export function renderLoading() {
  return <></>;
}

export function renderError(stationName, stationId, error) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "50vh",
        p: 3,
      }}
    >
      <Paper
        sx={{
          p: 4,
          textAlign: "center",
          maxWidth: 400,
          borderRadius: 2,
          border: "1px solid #e0e0e0",
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontWeight: 600,
            color: "error.main",
            mb: 2,
          }}
        >
          Weather Data Unavailable
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: "text.secondary",
            mb: 2,
          }}
        >
          {stationName || stationId}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: "text.secondary",
            opacity: 0.7,
          }}
        >
          Station data is currently not accessible
        </Typography>
      </Paper>
    </Box>
  );
}

/**
 * NoConfigurationMessage Component
 *
 * @param {string} communityId - The community ID
 * @param {Function} navigate - Navigation function from react-router
 * @returns {JSX.Element} The no configuration message component
 */
export default function NoConfigurationMessage({ communityId, navigate }) {
  const handleGoToSettings = () => {
    navigate(`/kiosk/${communityId}/setting`);
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "background.default",
        p: { xs: 1, sm: 3 },
        boxSizing: "border-box",
      }}
    >
      <Box sx={{ maxWidth: "70%", mx: "auto", width: "100%" }}>
        {/* Warning Card */}
        <Card
          elevation={8}
          sx={{
            border: "2px solid",
            borderColor: "warning.main",
            borderRadius: 3,
            background: "linear-gradient(135deg, #fff3e0 0%, #fff8e1 100%)",
            "&:hover": {
              boxShadow: "0 8px 25px rgba(255, 152, 0, 0.15)",
            },
          }}
        >
          <CardContent sx={{ p: 4 }}>
            <Stack direction="row" alignItems="flex-start" spacing={2}>
              <WarningIcon
                color="warning"
                sx={{
                  fontSize: 32,
                  mt: 0.5,
                  filter: "drop-shadow(0 2px 4px rgba(255, 152, 0, 0.2))",
                }}
              />
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    color: "warning.dark",
                    textShadow: "0 1px 2px rgba(0,0,0,0.1)",
                    letterSpacing: "-0.5px",
                    mb: 1,
                  }}
                >
                  No Configuration Found
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: "text.secondary",
                    lineHeight: 1.6,
                    opacity: 0.9,
                    fontSize: "1.1rem",
                    mb: 3,
                  }}
                >
                  The kiosk configuration has not been set up for this community
                  yet. You need to configure the slideshow settings before you
                  can use play mode.
                </Typography>

                <h6 sx={{ mb: 3 }}>
                  <Typography variant="body2">
                    <strong>What you need to do:</strong> Go to the settings
                    page to configure map displays, station selections, and
                    slideshow timers for this community.
                  </Typography>
                </h6>

                <Button
                  variant="contained"
                  size="large"
                  startIcon={<SettingsIcon />}
                  onClick={handleGoToSettings}
                  sx={{
                    py: 2,
                    px: 4,
                    borderRadius: 2,
                    fontSize: "1.1rem",
                    fontWeight: 600,
                    bgcolor: "warning.main",
                    "&:hover": {
                      bgcolor: "warning.dark",
                      transform: "translateY(-1px)",
                      boxShadow: "0 6px 20px rgba(255, 152, 0, 0.3)",
                    },
                    transition: "all 0.2s ease-in-out",
                  }}
                >
                  Go to Settings
                </Button>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}
